import type { AlxPassMetrics, AlxPassRow, CwCcwMode } from './types'

function mean(values: number[]): number | null {
  if (values.length === 0) return null
  return values.reduce((a, b) => a + b, 0) / values.length
}

function joinFileNames(names: string[]): string {
  return names.filter(Boolean).join(' + ')
}

function latestFinishTime(times: string[]): string | null {
  const vals = times.filter((t) => t && t.trim())
  if (vals.length === 0) return null
  return vals.reduce((a, b) => (a > b ? a : b))
}

function mergeMetricsGroup(rows: AlxPassRow[]): AlxPassMetrics {
  const metrics = rows.map((r) => r.metrics)
  const nums = (pick: (m: AlxPassMetrics) => number | null) =>
    metrics.map(pick).filter((v): v is number => v !== null)

  return {
    voltageMax: nums((m) => m.voltageMax).length
      ? Math.max(...nums((m) => m.voltageMax))
      : null,
    voltageMin: nums((m) => m.voltageMin).length
      ? Math.min(...nums((m) => m.voltageMin))
      : null,
    voltageAvg: mean(nums((m) => m.voltageAvg)),
    currentMax: nums((m) => m.currentMax).length
      ? Math.max(...nums((m) => m.currentMax))
      : null,
    currentMin: nums((m) => m.currentMin).length
      ? Math.min(...nums((m) => m.currentMin))
      : null,
    currentAvg: mean(nums((m) => m.currentAvg)),
    travelSpeedInchPerMin: mean(nums((m) => m.travelSpeedInchPerMin)),
    heatInputKJPerInch: mean(nums((m) => m.heatInputKJPerInch)),
    finishTime: latestFinishTime(
      metrics.map((m) => m.finishTime).filter((t): t is string => t !== null)
    )
  }
}

/** Collapse rows sharing the same pass name (merged CW/CCW mode). */
export function mergeDirectionalRows(rows: AlxPassRow[]): AlxPassRow[] {
  const groups = new Map<string, AlxPassRow[]>()
  for (const row of rows) {
    const list = groups.get(row.passName) ?? []
    list.push(row)
    groups.set(row.passName, list)
  }

  const merged: AlxPassRow[] = []
  groups.forEach((group, passName) => {
    merged.push({
      fileName: joinFileNames(group.map((r) => r.fileName)),
      passName,
      direction: null,
      metrics: mergeMetricsGroup(group),
      parseWarnings: group.flatMap((r) => r.parseWarnings)
    })
  })
  return merged
}

export function applyCwCcwMode(rows: AlxPassRow[], mode: CwCcwMode): AlxPassRow[] {
  if (mode === 'merged') return mergeDirectionalRows(rows)
  return rows
}

/** Mirror spot_check.py compute_expected for tests. */
export function computeExpectedMergedMetrics(perFile: AlxPassMetrics[]): AlxPassMetrics {
  return mergeMetricsGroup(
    perFile.map((metrics, i) => ({
      fileName: `file${i}.txt`,
      passName: 'test',
      direction: null,
      metrics,
      parseWarnings: []
    }))
  )
}

export const FLOAT_COMPARE_TOLERANCE = 0.05

export function valuesMatch(actual: unknown, expected: unknown): boolean {
  if (actual === null && expected === null) return true
  if (actual === null || expected === null) return false
  if (typeof expected === 'number' && typeof actual === 'number') {
    return Math.abs(actual - expected) <= FLOAT_COMPARE_TOLERANCE
  }
  return String(actual) === String(expected)
}
